import { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import {
  FileText,
  Download,
  Calendar,
  Briefcase,
  ArrowLeftRight,
} from "lucide-react";
import { useGetContactDetails } from "@/api/contact/useGetContactDetails";
import { useDownloadReport } from "@/api/reports/useDownloadReport";
import { Badge } from "@/components/badge";
import { Button } from "@/components/Button/Button";
import { Card, CardContent, CardHeader } from "@/components/Card/Card";
import { Checkbox } from "@/components/checkbox";
import { DatePicker } from "@/components/date-picker";
import { Label } from "@/components/label";
import { Loader } from "@/components/loader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/select";
import { Separator } from "@/components/separator";
import { Skeleton } from "@/components/skeleton";
import { useGlobalDateRange } from "@/hooks/useGlobalDateRange";
import { cn } from "@/lib/utils";
import { useConfig } from "@/providers/ConfigProvider";
import { dateToYYYYMMDD } from "@/utils/date";

dayjs.extend(quarterOfYear);

type DateRangeMode = "custom" | "preset";
type PresetPeriod =
  | "from-beginning"
  | "current-year"
  | "previous-year"
  | "current-month"
  | "previous-month"
  | "current-week"
  | "previous-week"
  | "current-quarter"
  | "previous-quarter"
  | "current-half"
  | "previous-half"
  | "ytd"
  | "last-30-days"
  | "last-90-days";

const PRESET_PERIODS: { value: PresetPeriod; label: string }[] = [
  { value: "from-beginning", label: "From Beginning" },
  { value: "ytd", label: "Year to Date" },
  { value: "current-year", label: "Current Year" },
  { value: "previous-year", label: "Previous Year" },
  { value: "current-month", label: "Current Month" },
  { value: "previous-month", label: "Previous Month" },
  { value: "current-week", label: "Current Week" },
  { value: "previous-week", label: "Previous Week" },
  { value: "current-quarter", label: "Current Quarter" },
  { value: "previous-quarter", label: "Previous Quarter" },
  { value: "current-half", label: "Current Half Year" },
  { value: "previous-half", label: "Previous Half Year" },
  { value: "last-30-days", label: "Last 30 Days" },
  { value: "last-90-days", label: "Last 90 Days" },
];

const getPresetDates = (
  preset: PresetPeriod
): { start: Dayjs | null; end: Dayjs } => {
  const today = dayjs();

  switch (preset) {
    case "from-beginning":
      return { start: null, end: today };

    case "ytd":
      return { start: today.startOf("year"), end: today };

    case "current-year":
      return { start: today.startOf("year"), end: today.endOf("year") };

    case "previous-year":
      return {
        start: today.subtract(1, "year").startOf("year"),
        end: today.subtract(1, "year").endOf("year"),
      };

    case "current-month":
      return { start: today.startOf("month"), end: today.endOf("month") };

    case "previous-month":
      return {
        start: today.subtract(1, "month").startOf("month"),
        end: today.subtract(1, "month").endOf("month"),
      };

    case "current-week":
      return { start: today.startOf("week"), end: today.endOf("week") };

    case "previous-week":
      return {
        start: today.subtract(1, "week").startOf("week"),
        end: today.subtract(1, "week").endOf("week"),
      };

    case "current-quarter":
      return { start: today.startOf("quarter"), end: today.endOf("quarter") };

    case "previous-quarter":
      return {
        start: today.subtract(1, "quarter").startOf("quarter"),
        end: today.subtract(1, "quarter").endOf("quarter"),
      };

    case "current-half": {
      const halfYear = today.month() < 6 ? 0 : 6;
      return {
        start: today.month(halfYear).startOf("month"),
        end: today.month(halfYear + 5).endOf("month"),
      };
    }

    case "previous-half": {
      const currentHalf = today.month() < 6 ? 0 : 6;
      const previousHalfStart = currentHalf === 0 ? 6 : 0;
      const yearOffset = currentHalf === 0 ? -1 : 0;
      return {
        start: today
          .add(yearOffset, "year")
          .month(previousHalfStart)
          .startOf("month"),
        end: today
          .add(yearOffset, "year")
          .month(previousHalfStart + 5)
          .endOf("month"),
      };
    }

    case "last-30-days":
      return { start: today.subtract(30, "days"), end: today };

    case "last-90-days":
      return { start: today.subtract(90, "days"), end: today };

    default:
      return { start: null, end: today };
  }
};

interface ReportProductionProps {
  contactId: number | string | undefined;
}
export const ReportProduction = (props: ReportProductionProps) => {
  const { contactId } = props;
  const { data, loading } = useGetContactDetails(contactId);

  const {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    reportDate,
    setReportDate,
  } = useGlobalDateRange();

  const { config } = useConfig();
  const [selectedReport, setSelectedReport] = useState<string>("");
  const [selectedPortfolios, setSelectedPortfolios] = useState<number[]>([]);
  const [dateRangeMode, setDateRangeMode] = useState<DateRangeMode>("preset");
  const [presetPeriod, setPresetPeriod] = useState<PresetPeriod>("ytd");
  const [purchaseValue, setPurchaseValue] = useState<0 | 1>(0);

  const purchaseValueType = (() => {
    if (!selectedReport) return false;
    const reportConfig = config?.reports.find(
      (r) => r.title === selectedReport
    );
    return (
      reportConfig?.reportDependencies?.includes("purchaseValueType") || false
    );
  })();

  const reportDateRequired = (() => {
    if (!selectedReport) return false;
    const reportConfig = config?.reports.find(
      (r) => r.title === selectedReport
    );
    return reportConfig?.reportDependencies?.includes("reportDate") || false;
  })();

  // Update dates when preset period changes
  useEffect(() => {
    if (dateRangeMode === "preset") {
      const { start, end } = getPresetDates(presetPeriod);
      setStartDate(start?.toDate() ?? undefined);
      setEndDate(end.toDate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetPeriod, dateRangeMode]);

  const handlePortfolioChange = (id: number) => {
    setSelectedPortfolios((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
  };

  const handleContactChange = () => {
    setSelectedPortfolios((prev) => {
      const activeIds =
        data?.portfolios.filter((x) => x.status === "A").map((x) => x.id) || [];
      if (activeIds.every((id) => prev.includes(id))) {
        return [];
      }
      return activeIds;
    });
  };

  useEffect(() => {
    setSelectedPortfolios(
      data?.portfolios
        .filter((x) => x.shortName.startsWith("2") && x.status === "A")
        .map((x) => x.id) || []
    );
  }, [data]);

  const getDataState = () => {
    if (selectedPortfolios.length === 0) {
      return "unchecked";
    }
    if (selectedPortfolios.length === data?.portfolios.length) {
      return "checked";
    }
    return "intermediate";
  };

  const { downloadReport, downloading } = useDownloadReport(
    selectedReport + ".pdf",
    false
  );
  const handleDownloadReport = () => {
    downloadReport(selectedReport, {
      portfolioId: selectedPortfolios.map((x) => x),
      ...(reportDateRequired
        ? { reportDate: dateToYYYYMMDD(reportDate) }
        : {
            startDate: dateToYYYYMMDD(startDate),
            endDate: dateToYYYYMMDD(endDate),
          }),
      ...(purchaseValueType && { purchaseValue }),
    });
  };

  const portfolios = data?.portfolios.filter((x) => x.status === "A") || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 items-start">
        {/* Left column: Report + Date + purchaseValueType */}
        <div className="space-y-4">
          {/* Report Selection Card */}
          <Card>
            <CardHeader icon={FileText} title="Select Report Type" />
            <CardContent className="space-y-0.5 max-h-[220px] overflow-y-auto">
              {config?.reports.map((report, i) => {
                const isSelected = selectedReport === report.title;
                return (
                  <label
                    key={i}
                    htmlFor={`report-${report.id}`}
                    className={cn(
                      "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-all duration-200",
                      isSelected
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-muted/50"
                    )}
                  >
                    <Checkbox
                      id={`report-${report.id}`}
                      checked={isSelected}
                      onCheckedChange={() =>
                        setSelectedReport((prev) => {
                          if (prev === report.title) {
                            return "";
                          }
                          return report.title;
                        })
                      }
                    />
                    <span className="text-xs font-medium">{report.title}</span>
                  </label>
                );
              })}
            </CardContent>
          </Card>

          {/* Date Range Card */}
          <Card>
            <CardHeader
              icon={Calendar}
              title={
                reportDateRequired ? "Select Report Date" : "Select Date Range"
              }
              actions={
                reportDateRequired ? undefined : (
                  <Tooltip
                    title={
                      dateRangeMode === "preset"
                        ? "Custom dates"
                        : "Preset periods"
                    }
                    placement="top"
                  >
                    <Button
                      size="xs"
                      variant="icon"
                      onClick={() =>
                        setDateRangeMode(
                          dateRangeMode === "preset" ? "custom" : "preset"
                        )
                      }
                    >
                      <ArrowLeftRight className="size-3.5" />
                    </Button>
                  </Tooltip>
                )
              }
            />
            <CardContent>
              {reportDateRequired ? (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide pr-4">
                    Report Date
                  </Label>
                  <DatePicker
                    date={reportDate}
                    setDate={setReportDate}
                    popoverClassName="z-[1400]"
                  />
                </div>
              ) : dateRangeMode === "preset" ? (
                <div className="space-y-3">
                  <Select
                    value={presetPeriod}
                    onValueChange={(value) =>
                      setPresetPeriod(value as PresetPeriod)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent className="z-[1400]">
                      {PRESET_PERIODS.map((period) => {
                        const { start, end } = getPresetDates(period.value);
                        const dateRange = start
                          ? `${start.format("DD.MM.YY")} - ${end.format(
                              "DD.MM.YY"
                            )}`
                          : `From beginning - ${end.format("DD.MM.YY")}`;
                        return (
                          <SelectItem key={period.value} value={period.value}>
                            <div className="flex items-center justify-between w-full gap-3">
                              <span>{period.label}</span>
                              <span className="text-xs text-muted-foreground">
                                {dateRange}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/50 border border-border text-xs text-muted-foreground">
                    <Calendar className="size-4 shrink-0" />
                    <span>
                      {(() => {
                        const { start, end } = getPresetDates(presetPeriod);
                        return start
                          ? `${start.format("DD.MM.YYYY")} - ${end.format(
                              "DD.MM.YYYY"
                            )}`
                          : `From beginning - ${end.format("DD.MM.YYYY")}`;
                      })()}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                      Start Date
                    </Label>
                    <DatePicker
                      date={startDate}
                      setDate={setStartDate}
                      popoverClassName="z-[1400]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                      End Date
                    </Label>
                    <DatePicker
                      date={endDate}
                      setDate={setEndDate}
                      popoverClassName="z-[1400]"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {purchaseValueType && (
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-4">
                  <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                    Purchase value
                  </span>
                  <div className="flex flex-col gap-1">
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                      <input
                        type="radio"
                        name="purchaseValue"
                        checked={purchaseValue === 0}
                        onChange={() => setPurchaseValue(0)}
                      />
                      Original purchase value
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                      <input
                        type="radio"
                        name="purchaseValue"
                        checked={purchaseValue === 1}
                        onChange={() => setPurchaseValue(1)}
                      />
                      Transaction purchase value
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column: Portfolios */}
        <Card>
          <CardHeader icon={Briefcase} title="Select Portfolios" />
          <CardContent className="p-0">
            {loading && !data && (
              <div className="space-y-1 p-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            )}
            {data && (
              <div className="flex flex-col">
                {/* Header: contact name + select all */}
                <div className="flex items-center justify-between px-2.5 py-1.5 border-b bg-muted/30">
                  <span className="text-[11px] font-semibold">{data.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground">
                      {selectedPortfolios.length}/{portfolios.length}
                    </span>
                    <button
                      type="button"
                      onClick={handleContactChange}
                      disabled={portfolios.length === 0}
                      className="text-[10px] text-primary hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {selectedPortfolios.length === portfolios.length &&
                      portfolios.length > 0
                        ? "Fjern alle"
                        : "Velg alle"}
                    </button>
                  </div>
                </div>

                {portfolios.length === 0 && (
                  <p className="text-[10px] text-muted-foreground px-2.5 py-3 text-center">
                    Ingen aktive porteføljer tilgjengelig.
                  </p>
                )}

                <div className="max-h-[380px] overflow-y-auto">
                  {portfolios.map((portfolio) => {
                    const isSelected = selectedPortfolios.includes(
                      portfolio.id
                    );
                    const modelName =
                      portfolio.modelPortfolio?.linkedPortfolio?.name ||
                      portfolio.modelPortfolio?.name;
                    return (
                      <label
                        key={portfolio.id}
                        htmlFor={`portfolio-${portfolio.id}`}
                        className={cn(
                          "flex items-center gap-2 px-2.5 py-1 cursor-pointer transition-colors",
                          isSelected
                            ? "bg-primary/5 hover:bg-primary/10"
                            : "hover:bg-muted/40"
                        )}
                      >
                        <Checkbox
                          id={`portfolio-${portfolio.id}`}
                          checked={isSelected}
                          onCheckedChange={() =>
                            handlePortfolioChange(portfolio.id)
                          }
                          className="size-3.5 shrink-0"
                        />
                        <span className="text-[10px] font-mono text-muted-foreground shrink-0 whitespace-nowrap pr-1">
                          {portfolio.shortName}
                        </span>
                        <span className="text-[11px] truncate flex-1 min-w-0">
                          {portfolio.name}
                          {modelName && (
                            <span className="text-muted-foreground ml-1">
                              · {modelName}
                            </span>
                          )}
                        </span>
                        {portfolio.status === "C" && (
                          <Badge
                            variant="secondary"
                            className="text-[9px] px-1 py-0 shrink-0"
                          >
                            Closed
                          </Badge>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Submit Button */}
      <div className="flex items-center justify-end gap-3">
        {downloading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader />
            <span>Generating report...</span>
          </div>
        )}
        <Button
          onClick={() => handleDownloadReport()}
          disabled={
            downloading ||
            !selectedReport ||
            selectedPortfolios.length === 0 ||
            (reportDateRequired ? !reportDate : !endDate)
          }
        >
          <Download className="size-4 mr-2" />
          Download Report
        </Button>
      </div>
    </div>
  );
};
